import { omit } from "lodash";
import {
  type ContextType,
  createContext,
  type ReactNode,
  useState,
} from "react";

export type FileMetadata = {
  size: number;
  contentType: string;
  fileName: string;
};

export const FileContext = createContext<{
  files:
    | Record<
        string,
        {
          id: string;
          fileMetadata: FileMetadata;
          progress?: number;
          state: "uploading" | "error" | "uploaded";
        }
      >
    | undefined;
  removeFile: (id: string) => void;
  pushFile: (id: string, fileMetadata: FileMetadata) => void;
  setFileUploadState: (
    id: string,
    state: "uploading" | "error" | "uploaded"
  ) => void;
  updateProgress: (id: string, percentage: number) => void;
  isUploading: boolean;
}>({
  files: undefined,
  removeFile: () => {},
  pushFile: () => {},
  setFileUploadState: () => {},
  updateProgress: () => {},
  isUploading: false,
});

export const FileContextProvider = ({ children }: { children: ReactNode }) => {
  const [files, setFiles] = useState<ContextType<typeof FileContext>["files"]>(
    {}
  );

  if (!files) {
    return null;
  }

  return (
    <FileContext.Provider
      value={{
        files,
        isUploading: Object.keys(files || {}).some(
          (id) => files[id]?.state === "uploading"
        ),
        pushFile: (id, fileMetadata) =>
          setFiles((prevFiles) => ({
            ...prevFiles,
            [id]: { id, fileMetadata, state: "uploading" },
          })),
        removeFile: (id: string) =>
          setFiles((prevFiles) => omit(prevFiles, id)),
        setFileUploadState: (id: string, state) =>
          setFiles((prevFiles) =>
            prevFiles?.[id]
              ? { ...prevFiles, [id]: { ...prevFiles[id], state } }
              : prevFiles
          ),
        updateProgress: (id, percentage) =>
          setFiles((prevFiles) =>
            prevFiles?.[id]
              ? {
                  ...prevFiles,
                  [id]: { ...prevFiles[id], progress: percentage },
                }
              : prevFiles
          ),
      }}
    >
      {children}
    </FileContext.Provider>
  );
};
